import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { GetProctoringRulesetById } from '@edtech-ui/core/models/proctoring-dashboard-models/get-proctoring-by-ruleset-id';
import { UpdateProctoringRulesetPayload } from '@edtech-ui/core/models/proctoring-dashboard-models/update-proctoring-ruleset';
import { ProctoringService } from '@edtech-ui/core/services/proctoring-dashboard/proctoring.service';
import { ProctoringConfigValidationUtils } from '@edtech-ui/core/utils/proctoring-dashboard/proctoring-config';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { ProctoringDeleteConfigComponent } from '../proctoring-delete-config/proctoring-delete-config.component';
import { MatDialog } from '@angular/material/dialog';
import { PostProctoringRulesetPayload } from '@edtech-ui/core/models/proctoring-dashboard-models/update-proctoring-rulesets';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ProctoringAddUpdateRuleDialogComponent } from '../proctoring-add-update-rule-dialog/proctoring-add-update-rule-dialog.component';
import { PROCTORED_RULES_NAME_DESCRIPTION } from '@edtech-ui/core/constants/recuritment-constants/proctoring-constants/ruleSets';
import { GetProctoringConfigurationsPagedRequest } from '@edtech-ui/core/models/proctoring-dashboard-models/get-proctroing-config';
import { DefultsRulesDialogBoxComponent } from '../defults-rules-dialog-box/defults-rules-dialog-box.component';
import { NonDefaultsDialogBoxComponent } from '../non-defaults-dialog-box/non-defaults-dialog-box.component';
@Component({
	selector: 'app-proctoring-rule-set-detail-page',
	standalone: true,
	imports: [CommonModule, FormsModule, RouterModule, MatProgressSpinnerModule,
		ReactiveFormsModule, HasPermissionDirective, MatTooltipModule
	],
	templateUrl: './proctoring-rule-set-detail-page.component.html',
	styleUrl: './proctoring-rule-set-detail-page.component.scss'
})
export class ProctoringRuleSetDetailPageComponent implements OnInit, OnDestroy {

	isEditing = false;
	isLoading = false;
	isSaving = false;
	subscription = new Subscription();
	ruleSetId = '';
	ruleSetDetails: GetProctoringRulesetById | null = null;
	rulesetEditForm: FormGroup;
	PermissionForStudents = PermissionForStudents;
	companyId = '';
	defaultConfigurationObj: any = null;

	constructor(
		private route: ActivatedRoute,
		private proctoringService: ProctoringService,
		private toastr: ToastrService,
		private dialog: MatDialog
	) {
		this.rulesetEditForm = new FormGroup(({
			name: new FormControl('', [Validators.required, ProctoringConfigValidationUtils.configurationNameValidator()]),
			description: new FormControl('', [ProctoringConfigValidationUtils.proctoringDescriptionValidator()]),
			isEnabled: new FormControl(),
			isDefault: new FormControl(false),
		}))
	}


	ngOnInit(): void {
		this.companyId = localStorage.getItem('company_id') || '';
		this.route.queryParamMap.subscribe(params => {
			this.isEditing = params.get('edit') === 'true';
		});

		this.ruleSetId = this.route.snapshot.paramMap.get('ruleSetId') || '';
		if (this.ruleSetId) {
			this.getProctoringConfiguration();
			this.getProctoringRulesetById();
		}
	}

	getProctoringConfiguration() {
		this.isLoading = true;
		const payload: GetProctoringConfigurationsPagedRequest = {
			page: 1,
			pageSize: 100,
			filters: {
				companyId: {
					equals: this.companyId
				},
				isDefault: {
					equals: true
				}
			},
			sort: [
				{
					field: 'createdAt',
					order: 'DESC'
				}
			],
			all: false
		};
		this.subscription.add(
			this.proctoringService.getProctoringConfiguration(payload).pipe(
				tap(response => {
					if (response.success) {
						const configData = response.data.data;
						this.defaultConfigurationObj = configData.length ? configData[0] : null;
						this.checkIsDefault();
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this.isLoading = false;
					return of(null);
				})
			).subscribe()
		);
	}

	checkIsDefault() {
		if (this.defaultConfigurationObj && this.ruleSetDetails) {
			const isDefault = this.defaultConfigurationObj.defaultRuleSetId === this.ruleSetId;
			this.rulesetEditForm.patchValue({ isDefault });
			if (this.ruleSetDetails) {
				this.ruleSetDetails.isDefault = isDefault;
			}
		}
	}

	getProctoringRulesetById() {
		this.isLoading = true;
		this.subscription.add(
			this.proctoringService.getProctoringRulesetById(this.ruleSetId).pipe(
				tap(response => {
					if (response.success) {
						this.ruleSetDetails = response.data;
						this.checkIsDefault();

						this.rulesetEditForm.patchValue({
							name: response.data.name,
							description: response.data.description,
							isEnabled: response.data.isEnabled,
							isDefault: this.ruleSetDetails.isDefault || false
						});
					} else {
						this.toastr.error(response.message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					return of(null);
				}),
			).subscribe()
		)
	}


	updateProctoringRuleset() {

		if (!this.isEditing) {
			this.isEditing = true;
			this.rulesetEditForm.patchValue({
				name: this.ruleSetDetails?.name,
				description: this.ruleSetDetails?.description,
				isEnabled: this.ruleSetDetails?.isEnabled
			});
			return;
		}

		if (this.rulesetEditForm.invalid) {
			this.rulesetEditForm.markAllAsTouched();
			return;
		}

		const payload: UpdateProctoringRulesetPayload = {
			...this.rulesetEditForm.value
		}

		this.isSaving = true;

		let update$ = this.proctoringService.updateProctoringRuleset(payload, this.ruleSetId);

		this.subscription.add(
			update$.pipe(
				tap((response: any) => {
					if (response.success) {
						this.toastr.success('Ruleset updated successfully');
						this.getProctoringRulesetById();
						this.isEditing = false;
					} else {
						this.toastr.error(response.message);
					}
				}),
				finalize(() => {
					this.isSaving = false;
				}),
				catchError(() => {
					return of(null);
				}),
			).subscribe()
		)
	}

	toggleEdit() {
		this.isEditing = !this.isEditing;
	}


	openNonDefaultDialogBox() {
		this.dialog.open(NonDefaultsDialogBoxComponent, {
			data: {
				type: 'ruleset'
			}
		})
	}

	openDefaultsDialogBox(): void {
		if (!this.isEditing) {
			return;
		}

		const currentValue = this.rulesetEditForm.get('isDefault')?.value;
		if (currentValue) {
			this.openNonDefaultDialogBox();
			return;
		}

		const dialogRef = this.dialog.open(DefultsRulesDialogBoxComponent, {
			width: '500px',
			disableClose: true,
			data: {
				type: 'ruleset'
			}
		});

		dialogRef.afterClosed().subscribe((result: boolean) => {
			if (result && this.defaultConfigurationObj) {
				const configPayload = {
					companyId: this.defaultConfigurationObj.companyId,
					name: this.defaultConfigurationObj.name,
					description: this.defaultConfigurationObj.description,
					isEnabled: this.defaultConfigurationObj.isEnabled,
					isDefault: this.defaultConfigurationObj.isDefault,
					allowedBrowsers: this.defaultConfigurationObj.allowedBrowsers,
					allowedDevices: this.defaultConfigurationObj.allowedDevices,
					requireCamera: this.defaultConfigurationObj.requireCamera,
					requireMicrophone: this.defaultConfigurationObj.requireMicrophone,
					requireScreenShare: this.defaultConfigurationObj.requireScreenShare,
					requireFullscreen: this.defaultConfigurationObj.requireFullscreen,
					recordCamera: this.defaultConfigurationObj.recordCamera,
					recordScreen: this.defaultConfigurationObj.recordScreen,
					recordAudio: this.defaultConfigurationObj.recordAudio,
					maxMedium: this.defaultConfigurationObj.maxMedium,
					maxHigh: this.defaultConfigurationObj.maxHigh,
					maxCritical: this.defaultConfigurationObj.maxCritical,
					defaultRuleSetId: this.ruleSetId
				};

				this.isSaving = true;
				this.subscription.add(
					this.proctoringService.updateProctoringConfigById(this.defaultConfigurationObj.configId, configPayload).pipe(
						tap(response => {
							if (response.success) {
								this.toastr.success('Set as default successfully');
								this.rulesetEditForm.patchValue({ isDefault: true });
								if (this.ruleSetDetails) {
									this.ruleSetDetails.isDefault = true;
								}
								this.defaultConfigurationObj.defaultRuleSetId = this.ruleSetId;
							} else {
								this.toastr.error(response.message);
							}
						}),
						finalize(() => {
							this.isSaving = false;
						}),
						catchError(() => {
							return of(null);
						})
					).subscribe()
				);
			}
		});
	}


	updateProctoringRulesetById(rule: any) {
		if (rule.debounceSeconds < 1 || rule.debounceSeconds > 300) {
			this.toastr.error('Debounce must be between 1 and 300 seconds.');
			return;
		}
		if (rule.thresholdWindowSeconds < 5 || rule.thresholdWindowSeconds > 600) {
			this.toastr.error('Window must be between 5 and 600 seconds.');
			return;
		}
		if (rule.thresholdWindowSeconds < rule.debounceSeconds) {
			this.toastr.error('Window must be greater than or equal to Debounce.');
			return;
		}
		if (rule.thresholdCount < 1 || rule.thresholdCount > 100) {
			this.toastr.error('Threshold must be between 1 and 100.');
			return;
		}
		if (rule.thresholdCount > rule.thresholdWindowSeconds) {
			this.toastr.error('Threshold cannot exceed Window limit.');
			return;
		}

		const payload: PostProctoringRulesetPayload = {
			ruleName: rule.ruleName,
			ruleType: rule.ruleType,
			description: rule.description,
			severity: rule.severity,
			thresholdCount: rule.thresholdCount,
			thresholdWindowSeconds: rule.thresholdWindowSeconds,
			debounceSeconds: rule.debounceSeconds,
			escalationAction: rule.escalationAction,
			captureSnapshot: rule.captureSnapshot,
			analysisSource: rule.analysisSource,
			isEnabled: rule.isEnabled
		};

		this.isSaving = true;
		this.subscription.add(
			this.proctoringService.updateProctoringRulesetById(rule.ruleSetId, rule.ruleId, payload).pipe(
				tap(response => {
					if (response.success) {
						this.toastr.success(response.message);
						this.getProctoringRulesetById();
					} else {
						this.toastr.error(response.message);
					}
				}),
				finalize(() => {
					this.isSaving = false;
				}),
				catchError(() => {
					return of(null);
				}),
			).subscribe()
		)
	}


	deleteProctoringRulesetById(rulesetId: string, ruleId: string) {
		this.subscription.add(
			this.proctoringService.deleteProctoringRulesetById(rulesetId, ruleId).pipe(
				tap(response => {
					if (response.success) {
						this.toastr.success(response.message);
						this.getProctoringRulesetById();
					} else {
						this.toastr.error(response.message);
					}
				}),
				catchError(() => {
					return of(null);
				})
			).subscribe()
		);
	}


	openDeleteDialogBox(rulesetId: string, ruleId: string, name: string) {
		const dialogRef = this.dialog.open(ProctoringDeleteConfigComponent, {
			data: {
				name,
				type: 'rule'
			}
		});

		dialogRef.afterClosed().subscribe(result => {
			if (!result) {
				return;
			}
			this.deleteProctoringRulesetById(rulesetId, ruleId);
		});
	}


	preventNonNumericalInput(event: KeyboardEvent): void {
		const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'];
		if (allowedKeys.includes(event.key)) {
			return;
		}
		if (!/^[0-9]$/.test(event.key)) {
			event.preventDefault();
		}
	}


	get allRulesAdded(): boolean {
		return (this.ruleSetDetails?.rules?.length || 0) >= PROCTORED_RULES_NAME_DESCRIPTION.length;
	}


	openAddRuleDialog() {
		const dialogRef = this.dialog.open(ProctoringAddUpdateRuleDialogComponent, {
			width: '500px',
			panelClass: 'overflow-visible-dialog',
			data: { existingRules: this.ruleSetDetails?.rules || [] }
		});

		dialogRef.afterClosed().subscribe((newRules: any[]) => {
			if (newRules && newRules.length > 0) {
				this.isSaving = true;
				this.subscription.add(
					this.proctoringService.postNewUpdateProctoringRule(this.ruleSetId, newRules).pipe(
						tap(() => {
							this.toastr.success('Rules added successfully');
							this.getProctoringRulesetById();
						}),
						finalize(() => {
							this.isSaving = false;
						}),
						catchError(() => {
							return of(null);
						})
					).subscribe()
				);
			}
		});
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}
